import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  Building2,
  BarChart3,
  CreditCard,
  LogOut,
  Settings,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';

/**
 * Chrome for {@code /platform/**}. Dark sidebar so the platform admin
 * feels like a tool rather than a marketing surface — same aesthetic
 * family as the operator admin but explicitly branded "Platform Admin"
 * so Karim can tell at a glance which dashboard he's in.
 */
export default function PlatformAdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : 'P';

  return (
    <div className="flex min-h-screen bg-neutral-50">
      <aside className="hidden w-60 flex-shrink-0 flex-col border-r border-white/5 bg-surface-dark text-white md:flex">
        <div className="border-b border-white/10 p-5">
          <Link to="/platform" className="block">
            <p className="text-lg font-semibold tracking-tight text-brand">RekPlay</p>
            <p className="mt-0.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
              Platform Admin
            </p>
          </Link>
        </div>

        <nav className="flex-1 space-y-0.5 p-3">
          <NavItem to="/platform" icon={Building2} label="Tenants" exact />
          <NavItem to="/platform/onboarding" icon={UserPlus} label="Onboarding" />
          <NavItem to="/platform/connect" icon={CreditCard} label="Stripe Connect" />
          <NavItem to="/platform/alerts" icon={Bell} label="Alerts" />
          <NavItem to="/platform/reports" icon={BarChart3} label="Reports" />
          <NavItem to="/platform/settings" icon={Settings} label="Settings" />
        </nav>

        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/20 text-xs font-semibold text-brand">
              {initials || 'P'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-white">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="truncate text-[11px] text-white/50">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-md p-1.5 text-white/50 transition-colors hover:bg-white/5 hover:text-white/90"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile topbar — sidebar is desktop-only for this dashboard (Karim uses a laptop). */}
      <header className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-neutral-200 bg-surface-dark px-4 py-3 md:hidden">
        <Link to="/platform" className="text-base font-semibold text-brand">
          RekPlay · Platform
        </Link>
        <button
          type="button"
          onClick={handleLogout}
          className="text-xs text-white/70 hover:text-white"
        >
          Log out
        </button>
      </header>

      <main className="flex-1 overflow-x-auto pt-14 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({
  to,
  icon: Icon,
  label,
  exact = false,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  exact?: boolean;
}) {
  const { pathname } = useLocation();
  const isActive = exact ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      className={
        'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ' +
        (isActive
          ? 'bg-brand/15 font-medium text-brand'
          : 'text-white/60 hover:bg-white/5 hover:text-white/90')
      }
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {label}
    </Link>
  );
}
